/**
 * @since 2026-04-21
 * @author Francesco Parrella
 * @maintainer Francesco Parrella
 * @copyright AlgoTraders, All rights reserved
 *
 * Shared config for per-check override capabilities. Kept in sync manually
 * with the backend DEFAULT_* constants — when a new threshold becomes
 * overridable in a check, add it here too so the dialog exposes it.
 */

export const CHECK_THRESHOLD_OPTIONS = {
  SourceDisagreement: [
    { parameterName: "warnDeviation", label: "Warn deviation", defaultValue: 0.01,
      description: "Fractional deviation at which a source is flagged. Default 0.01 (1%)." },
    { parameterName: "errorDeviation", label: "Error deviation", defaultValue: 0.05,
      description: "Fractional deviation that escalates severity to warn. Default 0.05 (5%)." },
  ],
  GapCalendar: [
    { parameterName: "gapMaxCalendarDays", label: "Min gap size (days)", defaultValue: 7,
      description: "Gaps shorter than this number of calendar days are ignored." },
    { parameterName: "warnGapCount", label: "Warn gap count", defaultValue: 0,
      description: "Exact count that triggers INFO; more than this triggers WARN." },
  ],
  HistoryLength: [
    { parameterName: "minYears", label: "Minimum years", defaultValue: 10,
      description: "Minimum years of history required. Default 10." },
  ],
  PriceAdjustment: [
    { parameterName: "warnAdjJumpPct", label: "Warn jump %", defaultValue: 0.25,
      description: "Single-bar adjusted-close jump that counts as suspicious. Default 0.25 (25%)." },
    { parameterName: "errorAdjJumpPct", label: "Error jump %", defaultValue: 0.50,
      description: "Single-bar jump that escalates severity to warn. Default 0.50 (50%)." },
  ],
  FuturesRoll: [
    { parameterName: "warnRollJump", label: "Warn roll jump", defaultValue: 0.05,
      description: "Return on a roll date that counts as suspicious. Default 0.05 (5%)." },
    { parameterName: "errorRollJump", label: "Error roll jump", defaultValue: 0.15,
      description: "Return on a roll date that escalates severity to warn. Default 0.15 (15%)." },
  ],
  IntradayPattern: [
    { parameterName: "barsPerDayWarn", label: "Short-day ratio", defaultValue: 0.5,
      description: "Days with fewer bars than this fraction of the median are flagged. Default 0.5." },
  ],
  ReturnsDistribution: [
    { parameterName: "extremeSigma", label: "Extreme σ threshold", defaultValue: 50,
      description: "Robust z (MAD-scaled) at which a single bar is treated as extreme. Defaults: 50 for 1Day, 200 for 1Minute." },
    { parameterName: "minAbsReturn", label: "Min |return| floor", defaultValue: 0.25,
      description: "Bars below this absolute return are never extreme, even if robust-z is huge (filters bond-ETF / pegged-FX false positives). Defaults: 0.25 (25%) for 1Day, 0.10 (10%) for 1Minute." },
    { parameterName: "warnExtremeBarCount", label: "Warn extreme bar count", defaultValue: 5,
      description: "Number of extreme bars that triggers WARN. Defaults: 5 for 1Day, 30 for 1Minute." },
    { parameterName: "warnMaxRobustZ", label: "Warn max σ", defaultValue: 200,
      description: "If any single bar exceeds this robust z (and clears the |return| floor), trigger WARN. Defaults: 200 for 1Day, 1000 for 1Minute." },
  ],
};

// Which checks support each kind of override.
export const CHECK_SUPPORTS = {
  SourceDisagreement: { threshold: true, acknowledgment: true, dateRangeException: true },
  GapCalendar:        { threshold: true, acknowledgment: true, dateRangeException: false },
  HistoryLength:      { threshold: false, acknowledgment: true, dateRangeException: false },
  PriceAdjustment:    { threshold: true, acknowledgment: true, dateRangeException: true },
  FuturesRoll:        { threshold: true, acknowledgment: true, dateRangeException: true },
  IntradayPattern:    { threshold: true, acknowledgment: true, dateRangeException: true },
  ReturnsDistribution:{ threshold: true, acknowledgment: true, dateRangeException: true },
  // OhlcIntegrity is auto-cleaned in the reconciler (drop NaN/non-positive,
  // clip Low/High so O/C in [L,H]). Anything that still surfaces means data
  // slipped past the cleaner and warrants engineering follow-up — the only
  // user-facing response is Acknowledge.
  OhlcIntegrity:      { threshold: false, acknowledgment: true, dateRangeException: false },
};
